import Controller, { OnBeforeCancel, OnBeforeSubmit, OnBlur, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, CheckboxAPI, FieldAPI, FormAPI, InputAPI, ModalProps, ModalService, ToastService } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { FragmentNames, ScoreNrsCreateAliases, ScoreNrsEditAliases } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { Inject } from '@odinjs/odin';
import { Endpoint } from '../../../../constants/endpoints';
import { DialogType, ModalType, SimpleDialogParams } from '../../../../interfaces/simple-dialog';
import DialogService from '../../../../services/dialog-service';
import { SimpleDialog, } from '../../../dialog-helper';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';
import { LocatorTypes } from '../../../../controllers/locator/locator-controller';
import timeService from '../../../../services/time-service';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { DomainType, ScoreNRSResponse } from '../../score-types';
import { Value } from '@philips-internal/oswald-ui-core';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ApplicationRoute } from '../../../../constants/routes';
import calculateImcReturned from '../../../../utils/score-nrs-imc';

interface ResponseCreateScoreNRS {
  age?: number;
  diseaseSeverity?: number;
  dtEvaluation?: string;
  dtInactivation?: string;
  dtRelease?: string;
  dtUpdate?: string;
  dtUpdateNrec?: string;
  elementNumber?: number;
  height?: number;
  hour?: number;
  idScoreIndexes?: number;
  illnessUti?: string;
  imc?: number;
  justification?: string;
  levelAttention?: string;
  note?: string;
  nrAttendance?: number;
  nrSignature?: number;
  nrSignatureInactivation?: number;
  nutritionalStatus?: number;
  professionalCode?: string;
  punctuation?: number;
  reducedFeeding?: string;
  situation?: string;
  user?: string;
  userInactivation?: string;
  userNrec?: string;
  usualWeight?: number;
  weight?: number;
  weightLoss?: number;
  weightLossLastMonth?: string;
}

@Controller({component: ScoreNrsEditAliases.SCORE_NRS_ADD_LAYOUT, fragment: FragmentNames.SCORE_NRS_EDIT})
export default class ScoreNrsEditController extends BaseController {

  @Inject(ScoreNrsEditAliases.FORM_SCORE_NRS_CREATE)
  private formAPI!: FormAPI;

  @Inject(ScoreNrsEditAliases.ACTION_DELETE_SCORE_NRS)
  private actionDeleteAPI!: ActionAPI;

  @Inject(ScoreNrsEditAliases.ACTION_NEW_SCORE_NRS)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreNrsEditAliases.ACTION_REFERENCE_SCORE_NRS)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScoreNrsEditAliases.ACTION_RELEASE_SCORE_NRS)
  private actionReleaseAPI!: ActionAPI;

  @Inject(ScoreNrsEditAliases.INPUT_EVALUATION_DATE)
  private evaluationDateInputAPI!: InputAPI;

  @Inject(ScoreNrsEditAliases.INPUT_WEIGHT)
  private weightInputAPI!: InputAPI;

  @Inject(ScoreNrsEditAliases.INPUT_HEIGHT)
  private heightInputAPI!: InputAPI;

  @Inject(ScoreNrsEditAliases.INPUT_IMC)
  private imcInputAPI!: InputAPI;

  @Inject(ScoreNrsEditAliases.INPUT_USUAL_WEIGHT)
  private usualWeightInputAPI!: InputAPI;

  @Inject(ScoreNrsEditAliases.INPUT_WEIGHT_LOSS)
  private weightLossInputAPI!: InputAPI;

  @Inject(ScoreNrsEditAliases.FIELD_NUTRITIONAL_STATUS_DOMAIN)
  private nutritionalStatusDomainInputAPI!: FieldAPI;

  @Inject(ScoreNrsEditAliases.FIELD_DISEASE_SEVERITY_DOMAIN)
  private diseaseSeverityDomainInputAPI!: FieldAPI;

  @Inject(ScoreNrsEditAliases.CHECKBOX_IE_WEIGHT_LOSS)
  private weightLossLastMonthCheckboxAPI!: CheckboxAPI

  @Inject(ScoreNrsEditAliases.CHECKBOX_REDUCED_FEEDING)
  private reducedFeedingCheckboxAPI!: CheckboxAPI

  @Inject(ScoreNrsEditAliases.CHECKBOX_UTI)
  private utiCheckboxAPI!: CheckboxAPI

  private calculateWeightLoss(): void {
    const weight = Number(this.weightInputAPI.getValue());
    const usualWeight = Number(this.usualWeightInputAPI.getValue());

    if(!weight || !usualWeight) {
      this.weightLossInputAPI.setValue(0);
      return
    }
    if(Number.isFinite(weight) || Number.isFinite(usualWeight)){
      const weightLoss = (((usualWeight - weight) * 100) / usualWeight)
      this.weightLossInputAPI.setValue(weightLoss.toFixed(2))
    }
  }

  @OnMount({ target: ScoreNrsEditAliases.FORM_SCORE_NRS_CREATE })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_NRS
    const { encounterId, idScoreIndexes, patientId } = this.historyService.getCurrentRouteParams();
    const idScore = Number(idScoreIndexes);

    this.actionReleaseAPI.setEnabled(false);
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReferenceAPI.setEnabled(false);

    applicationContext.popNavigationItem();
    this.formAPI.setLoading(true);
    this.loadEditionData(idScore)

    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
  }

  @OnUnmount({ target: ScoreNrsEditAliases.FORM_SCORE_NRS_CREATE })
  handleUnMountForm(): void {
    this.clearAbortController();
  }

  @OnMount({target: ScoreNrsEditAliases.INPUT_EVALUATION_DATE})
  handleInputMount(): void {
    timeService.getCurrentDateTime(this.setAbortController())
      .then(date => this.evaluationDateInputAPI.setValue(date))
      .catch(() => this.evaluationDateInputAPI.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  private loadEditionData(sequenceNumber: number): void {
    const abortController = this.setAbortController();
    HttpService.get(Endpoint.SCORE_NRS, { sequenceNumber }, { abortController })
      .then(response => {
        const record = response.body as ScoreNRSResponse;
        this.formAPI.setEditing(true);
        this.formAPI.validate();
        this.formAPI.setData(record as unknown as Record<string, ScoreNRSResponse>)

        const dateEvaluation = record.evaluationDate;
        const uti = BooleanUtils.isTrueValue(record.uti)
        const weightLossLastMonth = BooleanUtils.isTrueValue(record.weightLossLastMonth)
        const reducedFeeding = BooleanUtils.isTrueValue(record.reducedFeeding)

        this.formAPI.setData({ ...record, dateEvaluation, reducedFeeding, uti, weightLossLastMonth})

        this.diseaseSeverityDomainInputAPI.setValue(record.diseaseSeverityDomain)
        this.nutritionalStatusDomainInputAPI.setValue(record.nutritionalStatusDomain)

        this.utiCheckboxAPI.setValue(uti)
        this.weightLossLastMonthCheckboxAPI.setValue(weightLossLastMonth)
        this.reducedFeedingCheckboxAPI.setValue(reducedFeeding)

      }).catch((err) => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formAPI.setLoading(false);
        this.actionDeleteAPI.setEnabled(true);
        this.actionReferenceAPI.setEnabled(true);
        this.actionNewAPI.setEnabled(true);
        this.actionReleaseAPI.setEnabled(true);
      });
  }

  @OnBlur(ScoreNrsCreateAliases.INPUT_WEIGHT)
  handleIMCW(): void {
    this.calculateIMC();
    this.calculateWeightLoss();
  }

  @OnBlur(ScoreNrsCreateAliases.INPUT_HEIGHT)
  handleIMCH(): void {
    this.calculateIMC();
  }

  @OnBlur(ScoreNrsCreateAliases.INPUT_USUAL_WEIGHT)
  handleWeightLossU(): void {
    this.calculateWeightLoss();
  }

  @OnClick({ target: ScoreNrsEditAliases.ACTION_DELETE_SCORE_NRS })
  handleClickDelete(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();

    this.confirmDelete(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreNrsEditAliases.ACTION_NEW_SCORE_NRS })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.SCORE_NRS_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: ScoreNrsEditAliases.ACTION_RELEASE_SCORE_NRS })
  handleClickInactivate(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreNrsEditAliases.ACTION_REFERENCE_SCORE_NRS })
  handleClickReference(): void {
    this.getReference();
  }

  @OnBeforeSubmit({ target: ScoreNrsEditAliases.FORM_SCORE_NRS_CREATE })
  handleBeforeSubmit(): void {
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    const isCreated = this.context.get('isCreated');
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formData = this.formAPI.getRecord();
    const dtEvaluation = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);
    const age = formData.age || '';
    const weight = formData.weight || '';
    const weightLoss = formData.weightLoss || '';
    const height = formData.height || '';
    const imc = formData.imc || '';
    const usualWeight = formData.usualWeight || '';
    const nutritionalStatus = formData.nutritionalStatusDomain as DomainType;
    const diseaseSeverity = formData.diseaseSeverityDomain as DomainType;
    const note = formData.note || '';
    const illnessUti = BooleanUtils.isPositiveValue(formData.uti as boolean)
    const reducedFeeding = BooleanUtils.isPositiveValue(formData.reducedFeeding as boolean)
    const weightLossLastMonth = BooleanUtils.isPositiveValue(formData.weightLossLastMonth as boolean)

    const body = {
      age,
      diseaseSeverity: diseaseSeverity.valueDomain,
      dtEvaluation,
      height,
      idScoreIndexes: idScoreIndexes as number,
      illnessUti,
      imc,
      note,
      nrAttendance: encounterId as number,
      nutritionalStatus: nutritionalStatus.valueDomain,
      professionalCode: user?.physicalPersonId,
      reducedFeeding,
      usualWeight,
      weight,
      weightLoss,
      weightLossLastMonth,
    }

    this.createNewNrsScore(body);
  }

  @OnBeforeCancel(ScoreNrsEditAliases.FORM_SCORE_NRS_CREATE)
  handleCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }

  private createNewNrsScore(body: Record<string, Value>): void {
    this.formAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.post(Endpoint.POST_CREATE_SCORE_NRS, undefined, { abortController, body })
      .then(response => {
        const {idScoreIndexes} = response.body as ResponseCreateScoreNRS;
        this.showModalSubmit(idScoreIndexes as number);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formAPI.setLoading(false));
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseNrsScore(idScore); },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }
  private releaseNrsScore(sequenceNumber: number): void {
    this.formAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.put(Endpoint.RELEASE_SCORE_NRS, { sequenceNumber }, { abortController })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formAPI.setLoading(false));
  }

  private getReference(): void {
    const scoreType = 'nrs';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType
    }

    ModalService.openFullscreen(modalProperties);
  }

  private calculateIMC(): void {
    const weight = Number(this.weightInputAPI.getValue());
    const height = Number(this.heightInputAPI.getValue());
    let imc = 0;
    if(Number.isFinite(weight) || Number.isFinite(height)){
      imc = calculateImcReturned(weight,height);
    }
    this.imcInputAPI.setValue(imc)
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private confirmDelete(idScore: number): void {
    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.delete(idScore) },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  private delete(sequenceNumber: number): void {
    this.formAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.delete(Endpoint.SCORE_NRS, {
      sequenceNumber
    }, { abortController }).then(() => {
      this.formAPI.setLoading(false);
      applicationContext.popNavigationItem();
    }).catch((err) => {
      this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => this.formAPI.setLoading(false));
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => { this.goBack() },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  @OnUnmount(ScoreNrsEditAliases.SCORE_NRS_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_NRS;
  }
}
